import * as React from "react"
import "./heatmap.css"
import cn from "classnames"

import { useHeatmapState, SelectionChangeEventHandler, HeatmapState } from "./heatmap-state"
import { HeatmapFlightView } from "./heatmap-flight-view"
import { isEqual } from "nodash"
import { UploadPresentationOptions } from "@nova/constants"

export type { SelectionChangeEventHandler }

export type HeatmapStyle = {
    color: string
    selectedColor?: string
    disabled: boolean
    raised: boolean
    icon?: React.FunctionComponent<React.SVGAttributes<SVGElement>>
    state: string
    width?: string
}

function HeatmapTileUnMemoized<T extends { id: string }>(props: {
    item: T
    selected: boolean
    getStyle: (item: T) => HeatmapStyle
    isHovered: boolean
    setHoveredImageId: (id?: string) => void
}) {
    const { item, selected, getStyle, isHovered, setHoveredImageId } = props
    const style = getStyle(item)
    const handleHover = (hoveredId?: string) => {
        setHoveredImageId(hoveredId)
    }
    return (
        <li
            onMouseEnter={() => handleHover(item.id)}
            onMouseLeave={() => handleHover(undefined)}
            role="option"
            tabIndex={style.disabled ? undefined : 0}
            style={
                {
                    "--heatmap-tile-bg":
                        ((selected || isHovered) && style.selectedColor) || style.color,
                    width: style.width || "12px",
                } as React.CSSProperties
            }
            data-id={item.id}
            data-testid={item.id}
            aria-selected={selected}
            aria-disabled={style.disabled}
            className={cn("heatmap-tile hover:bg-black", {
                "heatmap-tile--raised": style.raised,
            })}
        >
            {style.icon && <style.icon className="heatmap-tile__icon" role="presentation" />}
        </li>
    )
}
const HeatmapTile = React.memo(HeatmapTileUnMemoized, (prevProps, nextProps) => {
    if (prevProps.selected !== nextProps.selected) return false
    if (prevProps.getStyle !== nextProps.getStyle) return false
    if (prevProps.isHovered !== nextProps.isHovered) return false
    if (prevProps.setHoveredImageId !== nextProps.setHoveredImageId) return false

    if (!isEqual(prevProps.item, nextProps.item)) return false

    return true
})

export type HeatmapProps<T extends { id: string }> = {
    items: T[]
    style: (instance: T, uploadPresentationOptions: UploadPresentationOptions) => HeatmapStyle
    selected: HeatmapState["selected"]
    updateSelected: (data: Record<string, boolean>, clickedId?: string) => void
    hoveredImageId?: string
    setHoveredImageId: (id?: string) => void
    isOverflowed?: boolean
    onDeleteImage?: () => void
    uploadPresentationOptions: UploadPresentationOptions
}

function isDisabled(t: HTMLElement | null) {
    return !t || t.getAttribute("aria-disabled") === "true"
}

function focusElement(t: Element | null) {
    if (t && "focus" in t && typeof t.focus === "function") {
        t.focus()
    }
}

export const HeatmapFlightViewDeprecated = HeatmapFlightView
export const Heatmap = <T extends { id: string }>({
    items,
    style,
    selected,
    updateSelected,
    hoveredImageId,
    setHoveredImageId,
    isOverflowed,
    onDeleteImage,
    uploadPresentationOptions,
}: HeatmapProps<T>) => {
    const { toggleSelection, select, deselect, setSelected } = useHeatmapState(
        selected,
        updateSelected
    )

    const [lastShiftSelected, setLastShiftSelected] = React.useState<number | null>(null)

    const onSelect = (e: React.MouseEvent | React.KeyboardEvent, t: HTMLLIElement | null) => {
        const tiles = getTiles()
        const i = t ? tiles.indexOf(t) : -1
        setLastShiftSelected(i)
        if (e.ctrlKey || e.metaKey) {
            toggle(tiles[i], true)
        } else if (e.shiftKey) {
            if (lastShiftSelected !== null) {
                const ids = []
                for (
                    let num = Math.min(i, lastShiftSelected);
                    num < Math.max(i, lastShiftSelected) + 1;
                    num++
                ) {
                    const id = tiles[num]?.dataset["id"]
                    if (id) ids.push(id)
                }
                select(ids, t?.dataset?.id ?? "")
            } else {
                if (t?.dataset.id) {
                    select([t.dataset.id], t.dataset.id)
                }
            }
        } else {
            toggle(t, e.shiftKey)
        }
    }

    const onClick = (e: React.MouseEvent) => {
        const t = e.target as HTMLLIElement | null
        onSelect(e, t)
    }

    const heatmapRef = React.useRef<HTMLUListElement>(null)
    // this grabs the list of tiles not from props but by querying the DOM, which lets us do things like focus a tile and similar without going back through React
    // it also automatically ignores disabled values
    const getTiles = () =>
        Array.from(
            heatmapRef.current?.querySelectorAll("li.heatmap-tile[aria-disabled=false]") || []
        ) as Array<HTMLLIElement>

    // this grabs the number of rows to skip
    const getRowLength = () => {
        const squareAmount = isOverflowed ? 13 : 14
        const width =
            Math.max(28 * squareAmount, heatmapRef.current?.getBoundingClientRect()?.width || 0) + 2
        return Math.floor(width / 28) // this comes from the CSS
    }

    const collectIds = (tiles: Array<HTMLLIElement>, from: number, to: number) => {
        const ids = [] as string[]
        for (let i = from; i <= to; i++) {
            const id = tiles[i]?.dataset["id"]
            if (id) ids.push(id)
        }
        return ids
    }

    const toggle = (t: HTMLElement | null, isAdditive: boolean) => {
        if (!t) {
            return
        }
        if (isDisabled(t)) return
        const id = t.dataset["id"]
        if (id != null) {
            if (isAdditive || (Object.keys(selected).length === 1 && selected[id])) {
                toggleSelection(id)
            } else {
                const test: Record<string, boolean> = {}
                test[id] = true
                setSelected(test, id)
            }
        }
    }

    const onKeyDown = (e: React.KeyboardEvent) => {
        const t = e.target as HTMLLIElement
        e.preventDefault()
        // these are all the keyboard interactions
        // following the recommended keyboard shortcuts in https://www.w3.org/WAI/ARIA/apg/patterns/grid/
        const action = {
            // selection
            " ": () => {
                onSelect(e, t)
            },
            home: () => {
                const tiles = getTiles()
                const i = tiles.indexOf(t)
                const rowlen = getRowLength()
                if (e.ctrlKey || e.metaKey) {
                    focusElement(tiles[0])
                    if (e.shiftKey) {
                        const ids = collectIds(tiles, 0, i)
                        select(ids, tiles[0]?.dataset?.id)
                    }
                } else {
                    focusElement(tiles[i - (i % rowlen)])
                    if (e.shiftKey) {
                        const ids = collectIds(tiles, i - (i % rowlen), i)
                        select(ids)
                    }
                }
            },
            end: () => {
                const tiles = getTiles()
                const i = tiles.indexOf(t)
                const rowlen = getRowLength()

                if (e.ctrlKey || e.metaKey) {
                    focusElement(tiles[tiles.length - 1])
                    if (e.shiftKey) {
                        const ids = collectIds(tiles, i, tiles.length - 1)
                        select(ids)
                    }
                } else {
                    const idx = Math.min(tiles.length - 1, i + (rowlen - (i % rowlen)) - 1)
                    focusElement(tiles[idx])
                    if (e.shiftKey) {
                        const ids = collectIds(tiles, i, idx)
                        select(ids)
                    }
                }
            },
            arrowright: () => {
                const tiles = getTiles()
                const i = tiles.indexOf(t)
                if (i < tiles.length - 1) {
                    const next = tiles[i + 1]
                    focusElement(next)
                    toggle(next as HTMLElement, e.shiftKey)
                }
            },
            arrowleft: () => {
                const tiles = getTiles()
                const i = tiles.indexOf(t)
                if (i > 0) {
                    const prev = tiles[i - 1]
                    focusElement(prev)
                    toggle(prev as HTMLElement, e.shiftKey)
                }
            },
            arrowdown: () => {
                // big advance
                const rowlen = getRowLength()
                const tiles = getTiles()
                const startIdx = tiles.indexOf(t)
                const endIdx = startIdx + rowlen
                if (startIdx + rowlen >= tiles.length) {
                    return
                }
                const destination = tiles[endIdx]
                focusElement(destination)
                if (e.shiftKey) {
                    const ids = collectIds(tiles, startIdx, endIdx)
                    select(ids)
                } else {
                    toggle(destination as HTMLElement, false)
                }
            },
            arrowup: () => {
                // big retreat
                const rowlen = getRowLength()
                const tiles = getTiles()
                const endIdx = tiles.indexOf(t)
                const startIdx = endIdx - rowlen

                if (startIdx < 0) {
                    return
                }
                const destination = tiles[startIdx]
                focusElement(destination)
                if (e.shiftKey) {
                    const ids = collectIds(tiles, startIdx, endIdx)
                    select(ids)
                } else {
                    toggle(destination as HTMLElement, false)
                }
            },
            a: () => {
                if (e.ctrlKey || e.metaKey) {
                    const tiles = getTiles()
                    const ids = collectIds(tiles, 0, tiles.length - 1)
                    select(ids)
                }
            },
            backspace: () => {
                onDeleteImage?.()
            },
            delete: () => {
                onDeleteImage?.()
            },
        }[e.key.toLowerCase()]

        if (action) {
            action()
            e.stopPropagation()
        }
    }

    // this effect handles the situation where a selected item transitions to be unselectable
    // in this case, it needs to be deselected
    React.useEffect(() => {
        const ids = [] as string[]
        for (const i of items) {
            if (style(i, uploadPresentationOptions).disabled && selected[i.id]) {
                ids.push(i.id)
            }
        }
        // handles the case of items being removed by a parent component
        // if an item is removed from the list, it should not be selected
        for (const [key, value] of Object.entries(selected)) {
            if (!value) continue
            if (!items.find((item) => item.id === key)) {
                ids.push(key)
            }
        }
        deselect(ids)
    }, [items, selected, deselect, toggleSelection, style, uploadPresentationOptions])

    return (
        <ul
            className="heatmap"
            role="grid"
            ref={heatmapRef}
            aria-multiselectable="true"
            onClick={onClick}
            onKeyDown={onKeyDown}
        >
            {items.map((i) => (
                <HeatmapTile
                    key={i.id}
                    item={i}
                    selected={!!selected[i.id]}
                    getStyle={(item) => style(item as T, uploadPresentationOptions)}
                    isHovered={hoveredImageId === i.id}
                    setHoveredImageId={setHoveredImageId}
                />
            ))}
        </ul>
    )
}
